import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Box, CircularProgress, Grid2, Stack, Typography } from "@mui/material";
import { EnhancedMembershipsList } from "../../../components/memberships-list/enhanced-memberships-list";
import { SubscriptionCard } from "../../../components/subscription-card";
import { useDesignerApplicationContext } from "../../../hooks/designer-application-context";
import { useHighLevelContext } from "../../../hooks/HighLevelContext";
import {
  getDesignerSmartGuildBillingSplit,
  getDesignerSmartGuildDisplayedFeatures,
} from "../../../utils/designer-smart-guild-pricing";
import {
  DesignerApplication,
  DesignerSubscriptionType,
  PaymentFrequency,
  SinglePaymentItems,
} from "../../../__generated__/types";
import {
  getMembershipTierFromPlanId,
  getPlanIdFromMembershipTier,
} from "../../../utils";

type MembershipSelectionProps = {
  readOnly?: boolean;
  additionalCards?: ReactNode[];
};

const getFrequencyFromTier = (
  tier?: DesignerSubscriptionType | null
): PaymentFrequency => {
  if (!tier) {
    return PaymentFrequency.Annually;
  }

  const value = tier.toString();

  if (value.includes("Monthly")) {
    return PaymentFrequency.Monthly;
  }

  if (value.includes("BiAnnually")) {
    return PaymentFrequency.BiAnnually;
  }

  return PaymentFrequency.Annually;
};

const getFrequencyFromPlan = (plan: any): PaymentFrequency => {
  const period = plan?.pricing?.period?.toLowerCase();

  switch (period) {
    case "monthly":
      return PaymentFrequency.Monthly;
    case "annually":
      return PaymentFrequency.Annually;
    case "bi-annually":
    case "biannually":
      return PaymentFrequency.BiAnnually;
    default:
      return PaymentFrequency.Annually;
  }
};

const toFriendlyLabel = (item: SinglePaymentItems | string) => {
  const value = typeof item === "string" ? item : String(item);
  return value
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/Bi Annually/gi, "Bi-Annually")
    .replace(/\bPlus\b/gi, "+");
};

const getDefaultDesignerPlanId = (plans: any[]): string | undefined => {
  const defaultPlanId = getPlanIdFromMembershipTier(
    DesignerSubscriptionType.Guild
  );

  if (defaultPlanId && plans.some((plan) => plan?._id === defaultPlanId)) {
    return defaultPlanId;
  }

  const smartGuildPlan = plans.find((plan) => {
    const metadata =
      plan?.metadata && typeof plan.metadata === "object"
        ? (plan.metadata as Record<string, unknown>)
        : {};

    return (
      String(metadata.audience ?? "").toLowerCase() === "designer" &&
      (String(metadata.bundleType ?? "").toLowerCase() ===
        "smart-guild-marketing" ||
        String(plan?.slug ?? "").toLowerCase().includes("smart-guild"))
    );
  });

  if (smartGuildPlan?._id) {
    return smartGuildPlan._id;
  }

  const designerPlans = plans.filter(
    (plan) => String(plan?.metadata?.audience ?? "").toLowerCase() === "designer"
  );

  if (designerPlans.length === 1) {
    return designerPlans[0]?._id;
  }

  return undefined;
};

export const MembershipSelectionComponent = ({
  readOnly = false,
  additionalCards,
}: MembershipSelectionProps) => {
  const designerContext = useDesignerApplicationContext();
  const application = (designerContext as any)
    .application as Partial<DesignerApplication> | null;
  const { getPlanList } = useHighLevelContext();

  const selectedTier = application?.membershipTier as
    | DesignerSubscriptionType
    | undefined;
  const selectedPlanIdFromContext =
    ((application as any)?.membershipPlanId as string | undefined) ??
    getPlanIdFromMembershipTier(selectedTier);

  const [selectedPlanLocal, setSelectedPlanLocal] = useState<
    string | undefined
  >(selectedPlanIdFromContext ?? undefined);

  const effectiveSelectedPlanId =
    selectedPlanLocal ?? selectedPlanIdFromContext ?? undefined;

  const [plans, setPlans] = useState<any[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const loadPlans = async () => {
      try {
        setIsLoadingPlans(true);
        const list = await getPlanList();
        if (!mounted) return;
        setPlans(list.items || []);
        setLoadError(null);
      } catch (error: any) {
        if (!mounted) return;
        setLoadError(error?.message || "Unable to load membership plans");
      } finally {
        if (mounted) {
          setIsLoadingPlans(false);
        }
      }
    };

    loadPlans();

    return () => {
      mounted = false;
    };
  }, [readOnly, getPlanList]);

  useEffect(() => {
    setSelectedPlanLocal(selectedPlanIdFromContext ?? undefined);
  }, [selectedPlanIdFromContext]);

  const handleSelect = useCallback(
    (planId?: string) => {
      const nextPlanId = planId ?? undefined;
      setSelectedPlanLocal(nextPlanId);

     if (!nextPlanId) {
        designerContext.updateApplicationData({
          membershipTier: undefined,
          membershipPlanId: undefined,
        });
        return;
      }

      // Find the plan to get its frequency
      const plan = plans.find(p => p._id === nextPlanId);
      const frequency = plan ? getFrequencyFromPlan(plan) : undefined;
      
      const tier = getMembershipTierFromPlanId(nextPlanId, frequency);
      if (!tier) {
        return;
      }

      designerContext.updateApplicationData({
        membershipTier: tier as unknown as DesignerSubscriptionType,
        membershipPlanId: nextPlanId,
      });
    },
    [designerContext, plans]
  );

  useEffect(() => {
    if (readOnly || effectiveSelectedPlanId || isLoadingPlans || loadError) {
      return;
    }

    const defaultPlanId = getDefaultDesignerPlanId(plans);
    if (!defaultPlanId) {
      return;
    }

    handleSelect(defaultPlanId);
  }, [
    effectiveSelectedPlanId,
    handleSelect,
    isLoadingPlans,
    loadError,
    plans,
    readOnly,
  ]);

  const selectedPlan = useMemo(() => {
    const planId = selectedPlanLocal ?? selectedPlanIdFromContext;
    if (!planId) {
      return undefined;
    }
    return plans.find((plan) => plan._id === planId);
  }, [plans, selectedPlanLocal, selectedPlanIdFromContext]);

  if (readOnly) {
    if (!effectiveSelectedPlanId) {
      return (
        <Typography variant="body2" color="text.secondary">
          No membership plan selected.
        </Typography>
      );
    }

    if (isLoadingPlans) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center">
          <CircularProgress size={48} sx={{ color: '#00A06D' }} />
        </Box>
      );
    }

    if (loadError) {
      return (
        <Typography variant="body2" color="error">
          {loadError}
        </Typography>
      );
    }

    if (!selectedPlan) {
      return (
        <Typography variant="body2" color="text.secondary">
          Selected membership plan could not be found. Please update your
          selection.
        </Typography>
      );
    }

    const frequency = getFrequencyFromPlan(selectedPlan);
    const isBeta = selectedPlan?.name?.toLowerCase().includes("beta");
    const singlePaymentItems = application?.singlePaymentItems || [];
    const designerSmartGuildSplit = getDesignerSmartGuildBillingSplit(selectedPlan);
    const smartGuildFeeFeatures = designerSmartGuildSplit
      ? [
          `Annual Marketing Fee: $${designerSmartGuildSplit.annualFeeAmount.toFixed(2)}`,
          `Monthly Platform Fee: $${designerSmartGuildSplit.monthlyFeeAmount.toFixed(2)}`,
          `Due Now: $${designerSmartGuildSplit.dueNowAmount.toFixed(2)}`,
        ]
      : [];
    const planFeatures = getDesignerSmartGuildDisplayedFeatures(
      selectedPlan,
      selectedPlan.benefits
    );
    const displayPrice = designerSmartGuildSplit
      ? designerSmartGuildSplit.totalAnnualFeeAmount
      : selectedPlan?.pricing?.price || 0;
    const planDescription = designerSmartGuildSplit
      ? undefined
      : selectedPlan.description;

    return (
      <Stack spacing={3}>
        <Grid2 container columns={12} spacing={2}>
          <Grid2 size={{ xs: 12, sm: 6, md: 4 }} maxWidth={368} minWidth={300}>
            <SubscriptionCard
              title={selectedPlan.name?.replace('BETA:', '').trim() || ''}
              features={planFeatures}
              plainFeatures={smartGuildFeeFeatures}
              description={planDescription}
              paymentFrequency={frequency}
              price={displayPrice}
              originalPrice={selectedPlan?.pricing?.originalPrice || null}
              selected
              selectable={false}
              className="my-account-subscription-card"
              isBeta={isBeta}
            />
          </Grid2>
        </Grid2>

        {singlePaymentItems.length > 0 && (
          <Stack spacing={1}>
            <Typography variant="subtitle1" fontWeight={600}>
              Additional Services Selected
            </Typography>
            {singlePaymentItems.map((item, index) => (
              <Typography key={`${item}-${index}`} variant="body2">
                {toFriendlyLabel(item as SinglePaymentItems)}
              </Typography>
            ))}
          </Stack>
        )}
      </Stack>
    );
  }


  return (
    <EnhancedMembershipsList
      variant="designer"
      showBetaPlans={false}
      showPlaceholders={false}
      showFrequencyToggle={false}
      selectedPlan={effectiveSelectedPlanId}
      onSelect={handleSelect}
      additionalCards={additionalCards}
      cardClassName="my-account-subscription-card"
      selectableCards={false}
      defaultFrequency={
        getFrequencyFromPlan(selectedPlan) ||
        getFrequencyFromTier(selectedTier)
      }
    />
  );
};

export default MembershipSelectionComponent;
